import React, { useEffect, useState } from 'react';
import ProductEditPanel from './ProductEditPanel';
import IngredientBreakdownChart from './IngredientBreakdownChart';
import Drawer from '../../common/Drawer';
import './ProductDetailPanel.css';

const ProductDetailPanel = ({ product, initialTab = 'overview', onClose, onSave, isSaving, validationErrors = {} }) => {
  const [tab, setTab] = useState(initialTab);

  useEffect(() => { setTab(initialTab); }, [product?.id, initialTab]);

  if (!product) return null;

  const fmt = (n) => (typeof n === 'number' ? `$${n.toFixed(2)}` : '$0.00');
  const fmtPct = (n) => (typeof n === 'number' ? `${(n * 100).toFixed(1)}%` : '—');

  const title = (
    <>
      <span className={`detail-status-dot ${product.is_active ? 'active' : 'inactive'}`} />
      {product.flavor}
    </>
  );

  const tabs = (
    <div className="detail-tabs">
      {['overview', 'ingredients', 'edit'].map((t) => (
        <button key={t} className={`detail-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
          {t.charAt(0).toUpperCase() + t.slice(1)}
        </button>
      ))}
    </div>
  );

  // Escape, overlay click and the close button are handled by <Drawer>. The
  // Edit tab's Save/Cancel live inside ProductEditPanel's form, so they're
  // pinned to the bottom via CSS rather than passed as `footer`.
  return (
    <Drawer
      isOpen
      onClose={onClose}
      title={title}
      ariaLabel={product.flavor}
      headerExtra={tabs}
      className="product-detail-drawer"
    >
        {tab === 'overview' && (
          <div className="detail-overview">
            <section className="detail-section">
              <h4>Cost Breakdown</h4>
              {[['Coffee', product.coffee_cost], ['Ingredients', product.ingredient_cost], ['Can', product.can_cost], ['Packing', product.packing_cost]].map(([label, val]) => (
                <div key={label} className="detail-row"><span>{label}</span><span>{fmt(val)}</span></div>
              ))}
              <div className="detail-row total"><span>Total COGS</span><span>{fmt(product.total_cogs)}</span></div>
            </section>

            <section className="detail-section">
              <h4>Pricing & Margins</h4>
              <div className="detail-row"><span>Wholesale Price</span><span>{fmt(product.wholesale_price)}</span></div>
              <div className="detail-row"><span>Retail Price</span><span>{fmt(product.retail_price)}</span></div>
              <div className="detail-row accent"><span>Wholesale Margin</span><span>{fmtPct(product.wholesale_margin)}</span></div>
              <div className="detail-row accent"><span>Retail Margin</span><span>{fmtPct(product.retail_margin)}</span></div>
            </section>

            <section className="detail-section">
              <h4>Volume & Spend</h4>
              <div className="detail-row"><span>Wholesale Volume</span><span>{(product.total_wholesale_volume ?? 0).toLocaleString()} cans</span></div>
              <div className="detail-row"><span>Coffee Spend</span><span>{fmt(product.coffee_spend)}</span></div>
              <div className="detail-row"><span>Ingredient Spend</span><span>{fmt(product.ingredient_spend)}</span></div>
            </section>

            {product.total_cogs > 0 && (
              <section className="detail-section">
                <h4>Cost Composition</h4>
                <div className="cost-bar">
                  {[['coffee', product.coffee_cost], ['ingredient', product.ingredient_cost], ['can', product.can_cost], ['packing', product.packing_cost]].map(([cls, val]) =>
                    val > 0 ? <div key={cls} className={`cost-seg ${cls}`} style={{ width: `${(val / product.total_cogs) * 100}%` }} title={`${cls}: ${fmt(val)}`} /> : null
                  )}
                </div>
                <div className="cost-bar-legend">
                  {['coffee', 'ingredient', 'can', 'packing'].map((cls) => (
                    <span key={cls}><i className={`legend-dot ${cls}`} />{cls.charAt(0).toUpperCase() + cls.slice(1)}</span>
                  ))}
                </div>
              </section>
            )}

            {product.notes && (
              <section className="detail-section">
                <h4>Notes</h4>
                <p className="detail-notes">{product.notes}</p>
              </section>
            )}
          </div>
        )}

        {tab === 'ingredients' && (
          <div className="detail-ingredients">
            {!product.ingredients?.length ? (
              <p className="detail-empty">No ingredients defined for this product.</p>
            ) : (
              <>
                {product.ingredients.map((ing) => (
                  <div key={ing.ingredient_id} className="ing-card">
                    <div className="ing-name">{ing.ingredient_name}</div>
                    <div className="ing-stats">
                      <div className="ing-stat"><span>Per Keg</span><strong>{ing.quantity_per_keg} {ing.unit}</strong></div>
                      <div className="ing-stat"><span>Per Can</span><strong>{ing.quantity_per_can} {ing.unit}</strong></div>
                      <div className="ing-stat"><span>Cost/unit</span><strong>${ing.cost_per_unit?.toFixed(4)}</strong></div>
                      <div className="ing-stat"><span>Cost/can</span><strong>${(ing.cost_per_unit * ing.quantity_per_can)?.toFixed(4)}</strong></div>
                    </div>
                  </div>
                ))}
                <div className="ing-chart-wrap">
                  <IngredientBreakdownChart ingredients={product.ingredients} />
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'edit' && (
          <ProductEditPanel
            product={product}
            onSave={onSave}
            onCancel={() => setTab('overview')}
            isSaving={isSaving}
            validationErrors={validationErrors}
          />
        )}
    </Drawer>
  );
};

export default ProductDetailPanel;
